import { useState, useMemo } from 'react'
import {
    VLPVariant,
    ApiShopperLandingPageLink,
    SelectedOptions,
    getInitialSelectedOptions,
    getActiveVariantAndImage,
    buildOptionKeyMap,
} from '@storefronts/common-types'

type UseVariantSelectionReturn = {
    selectedOptions: SelectedOptions
    setSelectedOptions: (options: SelectedOptions) => void
    activeVariant: VLPVariant | null
    variantImage: string
    variantId: string | null
}

export const useVariantSelection = (
    link: Required<ApiShopperLandingPageLink>,
    initialVariantId: string | null,
): UseVariantSelectionReturn => {
    const options = useMemo(() => link.optionInfo || [], [link.optionInfo])
    const optionKeyMap = useMemo(() => buildOptionKeyMap(options), [options])

    const selectedOptionsFor = (variantId: string | null) =>
        getInitialSelectedOptions({
            variants: link.variantInfo || [],
            options,
            optionKeyMap,
            selectedVariantId: variantId,
            defaultVariantId: link.renderedDefaultVariantId,
        })

    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
        () => selectedOptionsFor(initialVariantId),
    )
    // The URL variant arrives after hydration on statically generated pages.
    // Adopt it when the seed changes; clicks update selectedOptions directly.
    const [seededVariantId, setSeededVariantId] = useState(initialVariantId)
    if (seededVariantId !== initialVariantId) {
        setSeededVariantId(initialVariantId)
        setSelectedOptions(selectedOptionsFor(initialVariantId))
    }

    const { activeVariant, variantImage, variantId } = useMemo(() => {
        const variants = link.variantInfo || []
        const { activeVariant: variant, variantImage: image } =
            getActiveVariantAndImage(variants, selectedOptions, optionKeyMap, {
                fallbackImageUrl: link.imageUrl,
            })
        return {
            activeVariant: variant,
            variantImage: image ?? link.imageUrl,
            variantId: variant?.shopifyId ?? null,
        }
    }, [selectedOptions, link, optionKeyMap])

    return {
        selectedOptions,
        setSelectedOptions,
        activeVariant,
        variantImage,
        variantId,
    }
}
